import { DailyLog, UserProfile } from "@/types";

export const PROTEIN_ACTIVITY_FACTORS = {
    "Sedentary": 0.8,
    "Light": 1.0,
    "Moderate": 1.2,
    "Active": 1.4,
    "Very Active": 1.6
};

export const PROTEIN_GOAL_MULTIPLIERS: Record<string, number> = {
    "Weight Loss": 1.2,
    "Maintenance": 1.0,
    "Muscle Gain": 1.6,
    "Lean Muscle & Longevity": 1.5
};

export function calculateTargetProtein(profile: Partial<UserProfile>): number {
    if (profile.targetProtein) return profile.targetProtein;

    const weight = profile.weight || 70;
    const activity = profile.activityLevel || "Moderate";
    const goal = profile.goal || "Lean Muscle & Longevity";

    const activityFactor = PROTEIN_ACTIVITY_FACTORS[activity as keyof typeof PROTEIN_ACTIVITY_FACTORS] || 1.2;
    // Flexible matching for goal (contains check if exact match fails?)
    let goalMultiplier = PROTEIN_GOAL_MULTIPLIERS[goal] || 1.0;

    // Fallback logic if exact string doesn't match
    if (goalMultiplier === 1.0 && goal !== "Maintenance") {
        if (goal.toLowerCase().includes("muscle")) goalMultiplier = 1.5;
        else if (goal.toLowerCase().includes("loss") || goal.toLowerCase().includes("fat")) goalMultiplier = 1.2;
    }

    return Math.round(weight * activityFactor * goalMultiplier);
}

export function calculateLongevityAge(log: DailyLog | null, profile: Partial<UserProfile> = defaultProfile): number {
    const age = profile.age || 30;
    const weight = profile.weight || 70;
    const height = profile.height || 175;

    // 1. BMI Impact
    // BMI = weight (kg) / height (m)^2
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);

    // Ideal BMI range 18.5 - 24.9.
    // Deviation penalty: 0.15 years per point away from optimal range [20-23]
    let bmiPenalty = 0;
    if (bmi > 23) {
        bmiPenalty = (bmi - 23) * 0.15;
    } else if (bmi < 20) {
        bmiPenalty = (20 - bmi) * 0.15;
    }

    // 2. Lifestyle Impact (Daily Score)
    // Score 0-100. Neutral is 75. 
    // < 75 adds age. > 75 subtracts age.
    // Max impact +/- 5 years per day (instantaneous reading)
    const score = log ? calculateDailyScore(log) : 0;
    // (75 - 90) * 0.1 = -1.5 years
    // (75 - 50) * 0.1 = 2.5 years
    let lifestyleImpact = (75 - score) * 0.12;

    // 3. Specific Bad Habits Boosters
    if (log?.smoking?.type === "Yes") {
        lifestyleImpact += 2.0;
    }
    if ((log?.alcohol?.units || 0) > 2) {
        lifestyleImpact += 0.5;
    }
    const sleep = typeof log?.sleep === 'object' ? log.sleep.hours : (log?.sleep || 0);
    if (sleep < 6) {
        lifestyleImpact += 1.0;
    }

    // Dampen the result so it's not too volatile? 
    // Logic: Longevity Age is "If you lived like today every day".

    const longevityAge = age + bmiPenalty + lifestyleImpact;
    return parseFloat(longevityAge.toFixed(1));
}

export const defaultProfile: Partial<UserProfile> = {
    weight: 70, // kg
    age: 30,
    height: 175,
    gender: "Male",
    activityLevel: "Moderate",
    goal: "Lean Muscle & Longevity",
};

export function calculateCaloriesBurned(log: DailyLog, profile: Partial<UserProfile> = defaultProfile): number {
    // 1. Basal Metabolic Rate (BMR) - Mifflin-St Jeor Equation
    const height = profile.height || 175;
    const isMale = profile.gender === "Male";

    let bmr = (10 * (profile.weight || 70)) + (6.25 * height) - (5 * (profile.age || 30));
    bmr += isMale ? 5 : -161;

    // 2. Activity Multiplier (NEAT + Exercise)
    let activityCalories = 0;

    // Steps (~0.04 kcal per step)
    activityCalories += (log.steps || 0) * 0.04;

    // Workouts (Explicit calories or estimate from duration)
    (log.workouts || []).forEach(workout => {
        if ((workout.calories || 0) > 0) {
            activityCalories += workout.calories;
        } else if ((workout.duration || 0) > 0) {
            // Generic est: 6 kcal/min for moderate exercise
            activityCalories += workout.duration * 6;
        }
    });

    const tdee = (bmr * 1.1) + activityCalories;

    return Math.round(tdee);
}

export function calculateDailyProtein(log: DailyLog): number {
    const mealProtein = log.meals?.reduce((sum, meal) =>
        sum + (meal.items || []).reduce((s, i) => s + (i.protein || 0) * (i.quantity || 1), 0), 0
    ) || 0;
    const supplementProtein = log.supplementProtein || 0;
    return mealProtein + supplementProtein;
}

export function calculateCoreScore(log: DailyLog, profile: Partial<UserProfile> = defaultProfile) {
    let score = 0;

    // 1. Nutrition & Protein (Max 30)
    // Target: 150g or profile target or 2g/kg bodyweight
    // Target: Calculated based on profile
    const targetProtein = calculateTargetProtein(profile);
    const mealProtein = log.meals?.reduce((sum, meal) =>
        sum + (meal.items || []).reduce((s, i) => s + (i.protein || 0) * (i.quantity || 1), 0), 0
    ) || 0;
    const supplementProtein = log.supplementProtein || 0;
    const totalProtein = mealProtein + supplementProtein;

    score += Math.min(30, (totalProtein / targetProtein) * 30);

    // 2. Movement (Steps & NEAT) (Max 20)
    // Target: 10k steps
    const stepsTarget = 10000;
    score += Math.min(20, ((log.steps || 0) / stepsTarget) * 20);

    // 3. Sleep (Max 20)
    // Target: 8h
    const sleepTarget = 8;
    const sleepHours = typeof log.sleep === 'object' ? (log.sleep?.hours || 0) : (log.sleep || 0);
    score += Math.min(20, (sleepHours / sleepTarget) * 20);

    // 4. Workout & Recovery (Max 20)
    // Structured training: If any workout type != Rest -> +20
    const hasWorkout = (log.workouts || []).some(w => (w.type && w.type !== "Rest") || (w.duration && w.duration > 0));
    if (hasWorkout) {
        score += 20;
    }

    // 5. Hydration (Max 10)
    // Target: 3L
    const waterTarget = 3;
    score += Math.min(10, ((log.water || 0) / waterTarget) * 10);

    return Math.round(score);
}

export function calculateLifestyleBreakdown(log: DailyLog) {
    const breakdown = {
        alcohol: 0,
        smoking: 0,
        stress: 0,
        circadian: 0,
        social: 0,
        total: 0
    };

    // 1. Alcohol
    // 0 -> +5; 1-2 -> +4; 3-5 -> +2; 6-10 -> 0; >10 -> -5
    const drinks = log.alcohol?.units || 0;
    if (drinks === 0) breakdown.alcohol = 5;
    else if (drinks <= 2) breakdown.alcohol = 4;
    else if (drinks <= 5) breakdown.alcohol = 2;
    else if (drinks <= 10) breakdown.alcohol = 0;
    else breakdown.alcohol = -5;

    // 2. Smoking / Vaping
    // Cigarettes count. 0 -> +5.
    const cigs = log.smoking?.quantity || 0;
    if (cigs === 0) breakdown.smoking = 5;
    else if (cigs <= 2) breakdown.smoking = 0;
    else if (cigs <= 10) breakdown.smoking = -3;
    else breakdown.smoking = -10;

    // 3. Stress & Recovery
    // Stress 1-5. 1-2 -> +3; 3 -> +1; 4-5 -> -3.
    const stress = log.stress || 3;
    let stressScore = 0;
    if (stress <= 2) stressScore = 3;
    else if (stress === 3) stressScore = 1;
    else stressScore = -3;

    // Meditation offset
    const meditationMin = log.meditation || 0;
    if (meditationMin >= 20) stressScore += 2;
    else if (meditationMin >= 10) stressScore += 1;

    // Clamp stress score -5 to +5
    breakdown.stress = Math.max(-5, Math.min(5, stressScore));

    // 4. Circadian
    const sunlight = log.sunlight || 0;
    let circadianScore = 0;
    if (sunlight >= 40) circadianScore = 3;
    else if (sunlight >= 20) circadianScore = 2;
    else if (sunlight >= 10) circadianScore = 1;

    if (log.screenFreeBeforeBed) circadianScore += 1;

    // Cap at +3
    breakdown.circadian = Math.min(3, circadianScore);

    // 5. Social & Mood
    let socialScore = 0;
    const mood = log.mood || 3;

    if (typeof mood === 'number' || !isNaN(Number(mood))) {
        if (Number(mood) >= 4) socialScore += 1;
    }

    if (log.socialConnect) socialScore += 1;

    // Cap at +2
    breakdown.social = Math.min(2, socialScore);

    breakdown.total = breakdown.alcohol + breakdown.smoking + breakdown.stress + breakdown.circadian + breakdown.social;
    return breakdown;
}

export function calculateDailyScore(log: DailyLog): number {
    if (log.aiAnalysis && typeof log.aiAnalysis.day_score === 'number') {
        return log.aiAnalysis.day_score;
    }
    const core = calculateCoreScore(log);
    const lifestyle = calculateLifestyleBreakdown(log).total;

    const final = core + lifestyle;
    return Math.max(0, Math.min(100, Math.round(final)));
}

export function calculateLifestyleScore(log: DailyLog): number {
    return calculateLifestyleBreakdown(log).total;
}

export function generateFeedback(log: DailyLog, score: number, profile: Partial<UserProfile> = defaultProfile) {
    const positives: string[] = [];
    const negatives: string[] = [];
    const actions: string[] = [];

    const totalProtein = log.meals?.reduce((sum, meal) => sum + meal.items.reduce((s, i) => s + i.protein * i.quantity, 0), 0) || 0;
    const targetProtein = calculateTargetProtein(profile);

    // Protein Logic
    if (totalProtein >= targetProtein) {
        positives.push("Excellent protein intake.");
    } else if (totalProtein < targetProtein * 0.7) {
        negatives.push("Protein is quite low.");
        actions.push("Add a protein shake or chicken breast to dinner.");
    }

    // Sleep Logic
    const sleepHours = typeof log.sleep === 'object' ? (log.sleep?.hours || 0) : (log.sleep || 0);

    if (sleepHours >= 7.5) {
        positives.push("Good recovery sleep.");
    } else if (sleepHours < 6.5) {
        negatives.push(`Sleep (${sleepHours}h) is insufficient.`);
        actions.push("Aim for bed 1 hour earlier tonight.");
    }

    // Workout/Steps Logic
    if (log.workouts && log.workouts.some(w => w.type && w.type !== "Rest")) {
        const workoutTypes = log.workouts.filter(w => w.type !== "Rest").map(w => w.type).join(", ");
        positives.push(`Logged ${workoutTypes} session(s).`);
    } else if ((log.steps || 0) < 5000) {
        negatives.push("Activity level was low today.");
        actions.push("Go for a 15-minute walk.");
    }

    // Fallbacks
    if (positives.length === 0) positives.push("Logged data accurately.");
    if (actions.length === 0) actions.push("Maintain this consistency tomorrow!");

    return {
        score_reasons: { positives, negatives },
        tomorrow_actions: actions.slice(0, 3), // Limit to 3
        weekly_context_comment: score > 80
            ? "You're crushing it! This consistency will pay off."
            : "A decent day. Small improvements in sleep or diet will boost your score.",
        flags: {
            sleep_issue: sleepHours < 6,
            protein_low: totalProtein < 80,
            steps_low: (log.steps || 0) < 4000
        }
    };
}
